import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

const INDIAN_STATES = [
  "Andaman and Nicobar Islands","Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chandigarh",
  "Chhattisgarh","Dadra and Nagar Haveli and Daman and Diu","Delhi (NCT)","Goa","Gujarat","Haryana",
  "Himachal Pradesh","Jammu and Kashmir","Jharkhand","Karnataka","Kerala","Ladakh","Lakshadweep",
  "Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Puducherry",
  "Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand",
  "West Bengal",
];

const empty = {
  property_name: "", owner_name: "", stay_type: "PG",
  address_building_no: "", address_road_number: "", address_street: "", address_area: "",
  address_landmark: "", address_city: "", address_state: "", address_pincode: "",
  primary_email: "",
  primary_whatsapp: "", alt_mobile: "",
  gstin: "",
  year_established: "", num_buildings: "",
  total_rooms: "", total_beds: "", available_beds: "",
  accommodation_type: "Co-ed",
  password: "", confirm_password: "",
};

const CURRENT_YEAR = new Date().getFullYear();
const IN_MOBILE = /^[6-9]\d{9}$/;
// Keep only the 10 national digits; strips a pasted +91 / 91 / 0 prefix
const toLocalMobile = (raw) => {
  let d = String(raw || "").replace(/\D/g, "");
  while (d.length > 10 && (d.startsWith("91") || d.startsWith("0"))) d = d.slice(d.startsWith("0") ? 1 : 2);
  return d.slice(0, 10);
};

function MobileInput({ value, onChange, testid }) {
  return (
    <div className="flex">
      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-slate-50 text-sm text-slate-600 select-none" data-testid={`${testid}-prefix`}>+91</span>
      <Input
        type="tel" inputMode="numeric" value={value} placeholder="9812345678"
        className="rounded-l-none"
        onChange={(e) => onChange(toLocalMobile(e.target.value))}
        onPaste={(e) => { e.preventDefault(); onChange(toLocalMobile(e.clipboardData.getData("text"))); }}
        data-testid={testid}
      />
    </div>
  );
}

export default function RegisterModal({ open, onOpenChange }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [step, setStep] = useState("form");
  const [otp, setOtp] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [sentTo, setSentTo] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const validate = () => {
    const required = {
      property_name: "Property name",
      stay_type: "Type of stay",
      address_building_no: "Building No / H.No / Survey No",
      address_street: "Street Name",
      address_area: "Area",
      address_city: "City",
      address_state: "State",
      address_pincode: "Pincode",
      primary_email: "Email ID",
      primary_whatsapp: "Mobile Number (WhatsApp)",
      year_established: "Year established",
      total_rooms: "Total number of Rooms",
      total_beds: "Total number of beds",
      available_beds: "Current available beds",
      accommodation_type: "Accommodation type",
      password: "Password",
    };
    for (const [k, lbl] of Object.entries(required)) {
      if (form[k] === "" || form[k] === null || form[k] === undefined) return `Please fill ${lbl}`;
    }
    if (!/^(?=.*[A-Za-z])(?=.*\d)\S{8,}$/.test(form.password))
      return "Password must be at least 8 characters with letters and numbers";
    if (form.password !== form.confirm_password) return "Passwords do not match";
    if (!/^\d{6}$/.test(form.address_pincode)) return "Pincode must be 6 digits";
    if (!IN_MOBILE.test(form.primary_whatsapp)) return "Mobile Number must be 10 digits starting with 6-9";
    if (form.alt_mobile && !IN_MOBILE.test(form.alt_mobile)) return "Alternate mobile number must be 10 digits starting with 6-9";
    const year = parseInt(form.year_established, 10);
    if (isNaN(year) || year < 1900 || year > CURRENT_YEAR) return `Year established must be between 1900 and ${CURRENT_YEAR}`;
    if (form.num_buildings) {
      const b = parseInt(form.num_buildings, 10);
      if (isNaN(b) || b < 1 || b > 20) return "Number of buildings/blocks must be 1-20";
    }
    const rooms = parseInt(form.total_rooms, 10);
    if (isNaN(rooms) || rooms < 1 || rooms > 99) return "Total number of rooms must be 1-99";
    const beds = parseInt(form.total_beds, 10);
    if (isNaN(beds) || beds < 1 || beds > 300) return "Total beds/capacity must be 1-300";
    const avail = parseInt(form.available_beds, 10);
    if (isNaN(avail) || avail < 0 || avail > 50) return "Current available beds must be 0-50";
    if (avail > beds) return "Available beds cannot exceed total beds";
    return null;
  };

  const errMsg = (e, fallback) => {
    const detail = e?.response?.data?.detail;
    return (typeof detail === "string" ? detail : (Array.isArray(detail) ? detail[0]?.msg : null)) || fallback;
  };

  const sendOtp = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/register/send-otp", { email: form.primary_email, property_name: form.property_name });
      setStep("otp"); setOtp(""); setSentTo(form.primary_email.trim().toLowerCase());
      setCooldown(r.data.resend_after_seconds || 60);
      toast.success(`Verification code sent to ${form.primary_email}`);
    } catch (e) {
      toast.error(errMsg(e, "Could not send verification code"));
    } finally { setBusy(false); }
  };

  const submit = async () => {
    const err = validate();
    if (err) return toast.error(err);
    if (sentTo === form.primary_email.trim().toLowerCase() && cooldown > 0) return setStep("otp");
    await sendOtp();
  };

  const handleOpenChange = (v) => {
    if (!v) setStep("form");
    onOpenChange(v);
  };

  const verifyAndCreate = async () => {
    if (!/^\d{6}$/.test(otp)) return toast.error("Enter the 6-digit code from your email");
    setBusy(true);
    const payload = {
      ...form,
      email_otp: otp,
      confirm_password: undefined,
      primary_whatsapp: `+91${form.primary_whatsapp}`,
      alt_mobile: form.alt_mobile ? `+91${form.alt_mobile}` : null,
      year_established: parseInt(form.year_established, 10),
      num_buildings: form.num_buildings ? parseInt(form.num_buildings, 10) : null,
      total_rooms: parseInt(form.total_rooms, 10),
      total_beds: parseInt(form.total_beds, 10),
      available_beds: parseInt(form.available_beds, 10),
    };
    try {
      const r = await api.post("/auth/register", payload);
      login(r.data.token, r.data.owner);
      toast.success("Registered successfully!");
      onOpenChange(false);
      setForm(empty); setStep("form"); setOtp("");
      navigate("/dashboard");
    } catch (e) {
      toast.error(errMsg(e, "Registration failed"));
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (step === "otp") {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-md" data-testid="register-otp-dialog">
          <OtpStep
            email={form.primary_email} otp={otp} setOtp={setOtp} busy={busy} cooldown={cooldown}
            onVerify={verifyAndCreate} onResend={sendOtp} onBack={() => setStep("form")}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="register-modal-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Register your property</DialogTitle>
          <DialogDescription>Set up your PG / Hostel / Inn / Coliving in under a minute.</DialogDescription>
        </DialogHeader>

        {/* Section: Basic */}
        <Section title="Basic details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Property name" required><Input value={form.property_name} onChange={upd("property_name")} placeholder="e.g. Sunrise PG" data-testid="register-form-pg-name-input" /></F>
            <F label="Owner's name"><Input value={form.owner_name} onChange={upd("owner_name")} placeholder="e.g. Ravi Kumar" data-testid="register-form-owner-name-input" /></F>
            <F label="Type of stay" required>
              <Select value={form.stay_type} onValueChange={upd("stay_type")}>
                <SelectTrigger data-testid="register-form-stay-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PG">PG</SelectItem>
                  <SelectItem value="Hostel">Hostel</SelectItem>
                  <SelectItem value="Inn">Inn</SelectItem>
                  <SelectItem value="Coliving">Coliving</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </div>
        </Section>

        {/* Section: Address */}
        <Section title="Address">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Building No / H.No / Survey No" required><Input value={form.address_building_no} onChange={upd("address_building_no")} placeholder="12A" data-testid="register-form-building-no-input" /></F>
            <F label="Road Number"><Input value={form.address_road_number} onChange={upd("address_road_number")} placeholder="4" data-testid="register-form-road-no-input" /></F>
            <F label="Street Name" required><Input value={form.address_street} onChange={upd("address_street")} placeholder="Brigade Road" data-testid="register-form-street-input" /></F>
            <F label="Area" required><Input value={form.address_area} onChange={upd("address_area")} placeholder="Ashok Nagar" data-testid="register-form-area-input" /></F>
            <F label="Landmark"><Input value={form.address_landmark} onChange={upd("address_landmark")} placeholder="Near Metro Station" data-testid="register-form-landmark-input" /></F>
            <F label="City/Town" required><Input value={form.address_city} onChange={upd("address_city")} placeholder="Bangalore" data-testid="register-form-city-input" /></F>
            <F label="State" required>
              <Select value={form.address_state} onValueChange={upd("address_state")}>
                <SelectTrigger data-testid="register-form-state-input"><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Pincode" required><Input value={form.address_pincode} onChange={upd("address_pincode")} placeholder="560001" maxLength={6} data-testid="register-form-pincode-input" /></F>
          </div>
        </Section>

        {/* Section: Contact */}
        <Section title="Contact">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Email ID" required><Input type="email" value={form.primary_email} onChange={upd("primary_email")} placeholder="owner@example.com" data-testid="register-form-primary-email-input" /></F>
            <F label="Mobile Number (WhatsApp recommended)" required><MobileInput value={form.primary_whatsapp} onChange={upd("primary_whatsapp")} testid="register-form-primary-whatsapp-input" /></F>
            <F label="Alternate mobile number"><MobileInput value={form.alt_mobile} onChange={upd("alt_mobile")} testid="register-form-alt-mobile-input" /></F>
            <F label="GSTIN (if available)"><Input value={form.gstin} onChange={upd("gstin")} placeholder="29ABCDE1234F1Z5" data-testid="register-form-gstin-input" /></F>
          </div>
        </Section>

        {/* Section: Property specifics */}
        <Section title="Property specifics">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Year established" required>
              <Input type="number" min={1900} max={CURRENT_YEAR} value={form.year_established} onChange={upd("year_established")} placeholder="2018" data-testid="register-form-year-input" />
            </F>
            <F label="Number of buildings / blocks">
              <Input type="number" min={1} max={20} value={form.num_buildings} onChange={upd("num_buildings")} placeholder="2" data-testid="register-form-buildings-input" />
              <p className="text-[11px] text-slate-500">Valid values: 1 to 20</p>
            </F>
            <F label="Total number of Rooms" required>
              <Input type="number" min={1} max={99} value={form.total_rooms} onChange={upd("total_rooms")} placeholder="24" data-testid="register-form-total-rooms-input" />
              <p className="text-[11px] text-slate-500">Valid values: 1 to 99</p>
            </F>
            <F label="Total number of beds / capacity" required>
              <Input type="number" min={1} max={300} value={form.total_beds} onChange={upd("total_beds")} placeholder="60" data-testid="register-form-total-beds-input" />
              <p className="text-[11px] text-slate-500">Valid values: 1 to 300</p>
            </F>
            <F label="Current available beds" required>
              <Input type="number" min={0} max={50} value={form.available_beds} onChange={upd("available_beds")} placeholder="8" data-testid="register-form-available-beds-input" />
              <p className="text-[11px] text-slate-500">Valid values: 0 to 50</p>
            </F>
            <F label="Accommodation type" required>
              <Select value={form.accommodation_type} onValueChange={upd("accommodation_type")}>
                <SelectTrigger data-testid="register-form-accommodation-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                  <SelectItem value="Co-ed">Co-ed</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </div>
        </Section>

        {/* Section: Password */}
        <Section title="Account security">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Password" required>
              <div className="relative">
                <Input
                  type={showPw ? "text" : "password"}
                  value={form.password}
                  onChange={upd("password")}
                  placeholder="Min 8 characters, letters + numbers"
                  data-testid="register-form-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="register-form-password-toggle"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-[11px] text-slate-500">Min 8 characters, with letters and numbers (symbols like @ allowed)</p>
            </F>
            <F label="Confirm password" required>
              <div className="relative">
                <Input
                  type={showPw2 ? "text" : "password"}
                  value={form.confirm_password}
                  onChange={upd("confirm_password")}
                  placeholder="Re-enter your password"
                  data-testid="register-form-confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw2((v) => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="register-form-confirm-password-toggle"
                >
                  {showPw2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </F>
          </div>
        </Section>

        <div className="pt-5 flex justify-end gap-2 border-t border-slate-100">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="register-form-cancel-button">Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={submit} disabled={busy} data-testid="register-form-submit-button">
            {busy ? "Sending code…" : "Create account"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function OtpStep({ email, otp, setOtp, busy, cooldown, onVerify, onResend, onBack }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">Verify your email</DialogTitle>
        <DialogDescription>
          We sent a 6-digit code to <span className="font-semibold text-slate-800" data-testid="register-otp-email">{email}</span>. It expires in 5 minutes.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 pt-2">
        <Input
          inputMode="numeric" autoFocus maxLength={6} value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => e.key === "Enter" && onVerify()}
          placeholder="••••••" className="text-center text-2xl tracking-[0.5em] font-mono h-14"
          data-testid="register-otp-input"
        />
        <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" onClick={onVerify} disabled={busy} data-testid="register-otp-verify-button">
          {busy ? "Verifying…" : "Verify & create account"}
        </Button>
        <div className="flex items-center justify-between text-xs">
          <button className="text-slate-500 hover:underline" onClick={onBack} data-testid="register-otp-back-button">← Edit details</button>
          <button
            className="text-indigo-600 hover:underline disabled:text-slate-400 disabled:no-underline"
            onClick={onResend} disabled={busy || cooldown > 0} data-testid="register-otp-resend-button"
          >
            {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
          </button>
        </div>
        <p className="text-[11px] text-slate-500">Didn't get it? Check your spam folder.</p>
      </div>
    </>
  );
}

function Section({ title, children }) {
  return (
    <section className="pt-4">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-700 mb-3">{title}</p>
      {children}
    </section>
  );
}

function F({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
        {label}{required && <span className="text-rose-500 ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}
